import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import Logo from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, Check, Warning } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  if (!token) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-8">
        <div className="text-center">
          <Warning size={48} className="text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-zinc-900 mb-2">Link non valido</h2>
          <p className="text-zinc-500 mb-6">Il link per il reset della password non è valido.</p>
          <Link to="/forgot-password" className="text-zinc-900 font-bold hover:underline">
            Richiedi un nuovo link
          </Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Le password non corrispondono');
      return;
    }

    if (password.length < 6) {
      setError('La password deve essere di almeno 6 caratteri');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore nel reset della password');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-8">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check size={32} weight="bold" className="text-green-600" />
          </div>
          <h2 className="text-2xl font-black text-zinc-900 mb-4">Password Aggiornata</h2>
          <p className="text-zinc-500 mb-6">
            La tua password è stata aggiornata con successo. Ora puoi accedere con la nuova password.
          </p>
          <Button
            onClick={() => navigate('/login')}
            className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-bold uppercase tracking-wider"
            data-testid="go-to-login-button"
          >
            Vai al Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-8">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <Logo size="lg" />
        </div>

        <h2 className="text-2xl font-black text-zinc-900 mb-2 text-center">Nuova Password</h2>
        <p className="text-zinc-500 mb-8 text-center">
          Inserisci la tua nuova password
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 text-red-700 text-sm">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="password" className="text-sm font-bold uppercase tracking-wider text-zinc-700">
              Nuova Password
            </Label>
            <div className="relative">
              <Lock size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="rounded-none border-zinc-300 pl-10"
                placeholder="••••••••"
                data-testid="new-password-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword" className="text-sm font-bold uppercase tracking-wider text-zinc-700">
              Conferma Password
            </Label>
            <div className="relative">
              <Lock size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                className="rounded-none border-zinc-300 pl-10"
                placeholder="••••••••"
                data-testid="confirm-password-input"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-bold uppercase tracking-wider py-6"
            data-testid="reset-submit-button"
          >
            {loading ? 'Aggiornamento...' : 'Aggiorna Password'}
          </Button>
        </form>
      </div>
    </div>
  );
}
